/**
 * The three routes as a real tablist: `aria-selected` on the current tab and
 * roving tabindex so arrow keys move focus the way a native tab strip does.
 * The inspector's own filter row (inspector.tsx) is a `role="group"`, not a
 * tablist, because it filters a list in place rather than switching pages;
 * this one switches pages, so it earns the pattern the filter row does not.
 *
 * Underneath the ARIA role these are real `<a href>` elements with a click
 * handler that calls history.pushState, not buttons dispatching a callback.
 * A real href is what keeps middle-click, ctrl-click, and copy-link working;
 * intercepting only the plain left click is what keeps the app an SPA
 * instead of a full reload on every tab switch.
 */

import { useRef, type KeyboardEvent, type MouseEvent, type ReactElement } from 'react';

export interface NavTab {
  path: string;
  label: string;
}

export const NAV_TABS: NavTab[] = [
  { path: '/', label: 'Chat' },
  { path: '/settings', label: 'Settings' },
  { path: '/styleguide', label: 'Styleguide' },
];

export function NavTabs({
  current,
  onNavigate,
}: {
  current: string;
  onNavigate: (path: string) => void;
}): ReactElement {
  const refs = useRef<(HTMLAnchorElement | null)[]>([]);

  const focusIndex = (index: number): void => {
    const target = refs.current[(index + NAV_TABS.length) % NAV_TABS.length];
    target?.focus();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLAnchorElement>, index: number): void => {
    switch (event.key) {
      case 'ArrowRight':
        event.preventDefault();
        focusIndex(index + 1);
        break;
      case 'ArrowLeft':
        event.preventDefault();
        focusIndex(index - 1);
        break;
      case 'Home':
        event.preventDefault();
        focusIndex(0);
        break;
      case 'End':
        event.preventDefault();
        focusIndex(NAV_TABS.length - 1);
        break;
      default:
        break;
    }
  };

  const handleClick = (event: MouseEvent<HTMLAnchorElement>, path: string): void => {
    // A modified click (middle button, ctrl, cmd, shift, alt) is a request to
    // open in a new tab or window; leave the browser's own href handling in
    // place for those and only intercept the plain left click.
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }
    event.preventDefault();
    onNavigate(path);
  };

  return (
    <div className="nav-tabs" role="tablist" aria-label="Sections">
      {NAV_TABS.map((tab, index) => {
        const selected = tab.path === current;
        return (
          <a
            key={tab.path}
            ref={(el) => {
              refs.current[index] = el;
            }}
            href={tab.path}
            role="tab"
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            className={`nav-tabs__tab${selected ? ' nav-tabs__tab--active' : ''}`}
            onClick={(event) => {
              handleClick(event, tab.path);
            }}
            onKeyDown={(event) => {
              handleKeyDown(event, index);
            }}
          >
            {tab.label}
          </a>
        );
      })}
    </div>
  );
}
